import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiFileText, FiUsers, FiShield, FiClock } from 'react-icons/fi';
import './EngagementProcess.css';

const EngagementProcess = () => {
  const phases = [
    {
      number: '01',
      title: 'Initial Qualification',
      icon: <FiUsers />,
      description: 'Confidential discussion under NDA to assess mandate, capacity, and alignment.',
      details: [
        'Initial confidential consultation',
        'Execution of non-disclosure agreement',
        'Assessment of strategic objectives',
        'Evaluation of jurisdictional constraints',
        'Preliminary capacity verification'
      ]
    },
    {
      number: '02',
      title: 'Documentation Review',
      icon: <FiFileText />,
      description: 'Exchange of credentials, proof of funds, corporate structure, and regulatory standing.',
      details: [
        'Corporate structure verification',
        'Proof of financial capacity',
        'Regulatory compliance check',
        'Banking relationship validation',
        'Professional references review'
      ]
    },
    {
      number: '03',
      title: 'Strategic Coordination',
      icon: <FiShield />,
      description: 'Structured introductions, due diligence support, and transaction guidance through execution.',
      details: [
        'Structured counterparty introductions',
        'Due diligence coordination',
        'Transaction structuring guidance',
        'Jurisdiction selection advice',
        'Engagement sequencing'
      ]
    },
    {
      number: '04',
      title: 'Continuous Stewardship',
      icon: <FiClock />,
      description: 'Ongoing monitoring and value optimization for long-term partnerships.',
      details: [
        'Post-transaction monitoring',
        'Value optimization strategies',
        'Long-term partnership development',
        'Ongoing advisory support',
        'Market intelligence updates'
      ]
    }
  ];

  const requirements = [
    'Institutional investors, family offices, or operating principals with demonstrated transaction history',
    'Proof of financial capacity appropriate to asset class and transaction size',
    'Clear mandate, decision-making authority, and readiness to execute within defined timeframes',
    'Regulatory compliance and transparent corporate structure in all relevant jurisdictions',
    'Professional references and established banking relationships'
  ];

  return (
    <div className="engagement-process">
      {/* Hero Section */}
      <section className="process-hero">
        <div className="container">
          <h1 className="process-hero-title">Engagement Process</h1>
          <p className="process-hero-subtitle">
            A structured approach to strategic advisory and deal facilitation, ensuring alignment, 
            confidentiality, and successful execution at every stage.
          </p>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="timeline-header">
            <h2 className="section-title center">How We Engage</h2>
            <p className="section-subtitle center">
              Four distinct phases designed to ensure thorough qualification and successful execution
            </p>
          </div>

          <div className="timeline">
            {phases.map((phase, index) => (
              <div key={index} className="timeline-phase">
                <div className="phase-marker">
                  <div className="phase-number">{phase.number}</div>
                  <div className="phase-icon">{phase.icon}</div>
                </div>
                <div className="phase-content">
                  <h3 className="phase-title">{phase.title}</h3>
                  <p className="phase-description">{phase.description}</p>
                  <ul className="phase-details">
                    {phase.details.map((detail, i) => (
                      <li key={i}>
                        <FiCheckCircle className="detail-icon" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {index < phases.length - 1 && <div className="phase-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Standards */}
      <section className="standards-section">
        <div className="container">
          <div className="standards-grid">
            <div className="standards-content fade-in">
              <h2 className="section-title">Our Engagement Standards</h2>
              <p className="standards-text">
                All engagements are conducted with the highest levels of professionalism, 
                confidentiality, and institutional rigor. We maintain strict protocols to 
                ensure alignment with qualified counterparties.
              </p>
              <ul className="standards-list">
                <li>
                  <FiShield className="standard-icon" />
                  <div>
                    <h4>Strict Confidentiality</h4>
                    <p>All discussions protected by executed NDAs</p>
                  </div>
                </li>
                <li>
                  <FiCheckCircle className="standard-icon" />
                  <div>
                    <h4>Institutional Due Diligence</h4>
                    <p>Rigorous verification of credentials and capacity</p>
                  </div>
                </li>
                <li>
                  <FiUsers className="standard-icon" />
                  <div>
                    <h4>Qualified Counterparties Only</h4>
                    <p>Selective engagement with vetted participants</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="standards-visual slide-in-right">
              <div className="visual-card">
                <h3>Engagement Prerequisites</h3>
                <ul>
                  {requirements.map((req, index) => (
                    <li key={index}>
                      <FiCheckCircle className="req-icon" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Principles */}
      <section className="principles-section">
        <div className="container">
          <h2 className="section-title center">Core Engagement Principles</h2>
          <div className="principles-grid">
            <div className="principle-card">
              <div className="principle-number">01</div>
              <h3>Confidentiality First</h3>
              <p>All engagements begin with executed non-disclosure agreements and strict information protocols</p>
            </div>
            <div className="principle-card">
              <div className="principle-number">02</div>
              <h3>Independent Advisory</h3>
              <p>No proprietary positions, ensuring unbiased guidance aligned with client objectives</p>
            </div>
            <div className="principle-card">
              <div className="principle-number">03</div>
              <h3>Institutional Standards</h3>
              <p>Rigorous due diligence and structured approach to every engagement</p>
            </div>
            <div className="principle-card">
              <div className="principle-number">04</div>
              <h3>Cross-Border Expertise</h3>
              <p>Multi-jurisdictional capability with deep understanding of regional requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="process-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Begin the Conversation?</h2>
            <p>Qualified parties may submit a brief capability statement and proof of capacity under existing NDA</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">
                Initiate Confidential Discussion
                <FiArrowRight />
              </Link>
              <Link to="/counterparty-profile" className="btn-outline">
                View Counterparty Requirements
                <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngagementProcess;